/**
 * "Markkinayhteenveto — Suomen metsätilakaupat".
 *
 * 1:1 port of the mockup `#institutionSection` (vertailutyokalu.html:4272+,
 * `renderInstitutionView` at line 10435). Backend computes every aggregate
 * in a single `/smt/market/dashboard` call so the frontend only formats
 * numbers and emits HTML.
 *
 * UI structure (top→bottom):
 *
 *   .inst-header
 *     .inst-title-block   ← title + subtitle
 *     .inst-mode-toggle   ← "Myyntiin tulleet" / "Toteutuneet kaupat"
 *   .filter-bar           ← Maakunta · Kunta · Min/Max ha · Min m³/ha · Min € · Tyhjennä
 *   .inst-kpi-row         ← heading + 4 cards (count, ha, M€, €/ha)
 *   .inst-section         ← quarterly chart (8 quarters, twin bars, dual y-axes)
 *   .inst-section         ← yearly panels
 *   .inst-section         ← region table
 *   .inst-section         ← buyer mix (sold mode only)
 *
 * Auth: backend gates the dashboard endpoint to level ≥ 2. The header
 * already hides the `#institutionBtn` for lower-tier users; this view
 * surfaces a friendly 403 if a user somehow types `#/market` while signed
 * in at level<2.
 */
import { getMarketDashboard, type MarketDashboard, type MarketMode, type DashboardFilters } from '../api/market.ts';
import { APIRequestError } from '../api/client.ts';
import { currentUser } from '../state/session.ts';
import { REGIONS } from '../data/regions.ts';
import { t, getLang } from '../i18n/index.ts';

// ---------------------------------------------------------------------------
// Module state — mirrors the mockup's `INST_*` globals.
// ---------------------------------------------------------------------------

let INST_MODE: MarketMode = 'unsold';
let INST_REGIONS_SELECTED: string[] = [];
let LAST_DASHBOARD: MarketDashboard | null = null;
let LAST_ROOT: HTMLElement | null = null;

// Debounce token for filter inputs so we don't fire one request per keystroke.
let reloadTimer: number | null = null;

// ---------------------------------------------------------------------------
// Public entrypoint.
// ---------------------------------------------------------------------------

export async function renderMarket(root: HTMLElement): Promise<void> {
  LAST_ROOT = root;
  if (!currentUser()) {
    root.innerHTML = `<div class="simple-page"><div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(t('login_required'))}</div></div>`;
    return;
  }

  // Render the static shell once; subsequent reloads only touch the dynamic
  // panels inside the shell.
  root.innerHTML = shellHtml();
  populateRegionDropdown();
  bindShellEvents();
  await reload();
}

// ---------------------------------------------------------------------------
// Shell HTML — matches the mockup markup verbatim so the same .inst-* CSS
// rules apply unchanged.
// ---------------------------------------------------------------------------

function shellHtml(): string {
  return `
    <div class="listing-section inst-page">
      <div class="inst-header">
        <div class="inst-title-block">
          <div class="section-h">${escapeHtml(t('inst_title'))}</div>
          <div class="section-sub">${escapeHtml(t('inst_subtitle'))}</div>
        </div>
        <div class="inst-mode-toggle">
          <button class="inst-mode-btn ${INST_MODE === 'unsold' ? 'active' : ''}" data-mode="unsold">${escapeHtml(t('inst_mode_unsold'))}</button>
          <button class="inst-mode-btn ${INST_MODE === 'sold' ? 'active' : ''}" data-mode="sold">${escapeHtml(t('inst_mode_sold'))}</button>
        </div>
      </div>

      <div class="filter-bar inst-filter-bar">
        <div class="filter-group">
          <label class="filter-label">${escapeHtml(t('filter_region'))}</label>
          <div class="multi-select" id="instFilterRegion" tabindex="0">
            <span class="multi-select-text" id="instFilterRegionText">${escapeHtml(t('filter_all'))}</span>
            <span class="multi-select-caret">▾</span>
            <div class="multi-select-dropdown" id="instFilterRegionDropdown" style="display:none"></div>
          </div>
        </div>
        <div class="filter-group">
          <label class="filter-label">${escapeHtml(t('filter_kunta'))}</label>
          <input type="text" class="filter-input" id="instFilterKunta" placeholder="${escapeHtml(t('filter_kunta_ph'))}">
        </div>
        <div class="filter-group">
          <label class="filter-label">${escapeHtml(t('filter_min_ha_long'))}</label>
          <input type="number" class="filter-input" id="instFilterMinHa" placeholder="0" min="0" step="1">
        </div>
        <div class="filter-group">
          <label class="filter-label">${escapeHtml(t('filter_max_ha'))}</label>
          <input type="number" class="filter-input" id="instFilterMaxHa" placeholder="—" min="0" step="1">
        </div>
        <div class="filter-group">
          <label class="filter-label">${escapeHtml(t('filter_min_m3ha'))}</label>
          <input type="number" class="filter-input" id="instFilterMinM3" placeholder="0" min="0" step="1">
        </div>
        <div class="filter-group">
          <label class="filter-label">${escapeHtml(t('filter_min_price'))}</label>
          <input type="number" class="filter-input" id="instFilterMinPrice" placeholder="0" min="0" step="1000">
        </div>
        <button class="filter-reset" id="instFilterReset">${escapeHtml(t('filter_reset'))}</button>
      </div>

      <div class="inst-kpi-row" id="instKpiRow">${escapeHtml(t('view_admin_loading'))}</div>

      <div class="inst-section">
        <div class="inst-section-title">${escapeHtml(t('inst_quart_title'))}</div>
        <div class="inst-section-sub" id="instQuartSub"></div>
        <div class="inst-chart-wrap">
          <div id="instQuartChart" class="inst-bar-chart"></div>
          <div class="inst-chart-legend">
            <div class="inst-legend-item"><span class="inst-legend-dot" style="background:var(--good)"></span> <span>${escapeHtml(t('inst_legend_ha'))}</span></div>
            <div class="inst-legend-item"><span class="inst-legend-dot" style="background:var(--accent)"></span> <span>${escapeHtml(t('inst_legend_eur'))}</span></div>
          </div>
        </div>
      </div>

      <div class="inst-section">
        <div class="inst-section-title">${escapeHtml(t('inst_yearly_title'))}</div>
        <div class="inst-section-sub" id="instYearlySub"></div>
        <div id="instYearlyContainer"></div>
      </div>

      <div class="inst-section">
        <div class="inst-section-title">${escapeHtml(t('inst_region_title'))}</div>
        <div class="inst-section-sub">${escapeHtml(t('inst_region_sub'))}</div>
        <div id="instRegionTable"></div>
      </div>

      <div class="inst-section" id="instBuyerMixSection" style="display:none">
        <div class="inst-section-title">${escapeHtml(t('inst_buyer_title'))}</div>
        <div class="inst-section-sub">${escapeHtml(t('inst_buyer_sub'))}</div>
        <div id="instBuyerMixContainer"></div>
      </div>
    </div>
  `;
}

function populateRegionDropdown(): void {
  const dd = document.getElementById('instFilterRegionDropdown');
  if (!dd) return;
  dd.innerHTML = REGIONS.map(
    (r) => `<label class="multi-select-opt">
      <input type="checkbox" value="${escapeHtml(r.name)}"${INST_REGIONS_SELECTED.includes(r.name) ? ' checked' : ''}>
      <span>${escapeHtml(r.name)}</span>
    </label>`,
  ).join('');
  dd.querySelectorAll<HTMLInputElement>('input[type=checkbox]').forEach((cb) => {
    cb.addEventListener('change', () => {
      INST_REGIONS_SELECTED = Array.from(
        dd.querySelectorAll<HTMLInputElement>('input[type=checkbox]:checked'),
      ).map((c) => c.value);
      refreshRegionLabel();
      scheduleReload();
    });
  });
  refreshRegionLabel();
}

function refreshRegionLabel(): void {
  const txt = document.getElementById('instFilterRegionText');
  if (!txt) return;
  if (INST_REGIONS_SELECTED.length === 0) {
    txt.textContent = t('filter_all');
  } else if (INST_REGIONS_SELECTED.length === 1) {
    txt.textContent = INST_REGIONS_SELECTED[0];
  } else {
    txt.textContent = t('filter_n_regions', { n: INST_REGIONS_SELECTED.length });
  }
}

function bindShellEvents(): void {
  document.querySelectorAll<HTMLButtonElement>('.inst-mode-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const m = btn.dataset.mode as MarketMode | undefined;
      if (!m || m === INST_MODE) return;
      INST_MODE = m;
      document.querySelectorAll('.inst-mode-btn').forEach((b) => b.classList.toggle('active', b === btn));
      void reload();
    });
  });

  // Region dropdown open/close
  const wrap = document.getElementById('instFilterRegion');
  const dd = document.getElementById('instFilterRegionDropdown');
  if (wrap && dd) {
    wrap.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = dd.style.display !== 'none';
      dd.style.display = isOpen ? 'none' : 'block';
    });
    document.addEventListener('click', () => {
      dd.style.display = 'none';
    });
  }

  // Debounced numeric/text filter inputs
  ['instFilterKunta', 'instFilterMinHa', 'instFilterMaxHa', 'instFilterMinM3', 'instFilterMinPrice'].forEach((id) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el) el.addEventListener('input', scheduleReload);
  });

  document.getElementById('instFilterReset')?.addEventListener('click', () => {
    (document.getElementById('instFilterKunta') as HTMLInputElement).value = '';
    (document.getElementById('instFilterMinHa') as HTMLInputElement).value = '';
    (document.getElementById('instFilterMaxHa') as HTMLInputElement).value = '';
    (document.getElementById('instFilterMinM3') as HTMLInputElement).value = '';
    (document.getElementById('instFilterMinPrice') as HTMLInputElement).value = '';
    INST_REGIONS_SELECTED = [];
    document
      .querySelectorAll<HTMLInputElement>('#instFilterRegionDropdown input[type=checkbox]')
      .forEach((c) => {
        c.checked = false;
      });
    refreshRegionLabel();
    void reload();
  });
}

function scheduleReload(): void {
  if (reloadTimer != null) window.clearTimeout(reloadTimer);
  reloadTimer = window.setTimeout(() => {
    reloadTimer = null;
    void reload();
  }, 220);
}

function currentFilters(): DashboardFilters {
  const kunta = (document.getElementById('instFilterKunta') as HTMLInputElement | null)?.value || '';
  const numericVal = (id: string): number | null => {
    const v = (document.getElementById(id) as HTMLInputElement | null)?.value;
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    mode: INST_MODE,
    regions: INST_REGIONS_SELECTED.length > 0 ? INST_REGIONS_SELECTED : undefined,
    kunta: kunta || undefined,
    min_ha: numericVal('instFilterMinHa'),
    max_ha: numericVal('instFilterMaxHa'),
    min_m3: numericVal('instFilterMinM3'),
    min_price: numericVal('instFilterMinPrice'),
  };
}

async function reload(): Promise<void> {
  if (!LAST_ROOT) return;
  setLoading(true);
  try {
    const data = await getMarketDashboard(currentFilters());
    LAST_DASHBOARD = data;
    renderKpis(data);
    renderQuarters(data);
    renderYearly(data);
    renderRegions(data);
    renderBuyerMix(data);
  } catch (err) {
    showError(err);
  } finally {
    setLoading(false);
  }
}

function setLoading(on: boolean): void {
  const kpi = document.getElementById('instKpiRow');
  if (on && kpi && !LAST_DASHBOARD) {
    kpi.innerHTML = `<div class="placeholder-card">${escapeHtml(t('view_admin_loading'))}</div>`;
  }
}

function showError(err: unknown): void {
  const msg = err instanceof APIRequestError
    ? err.status === 403
      ? t('market_forbidden')
      : err.body?.error || err.message
    : (err as Error).message;
  const kpi = document.getElementById('instKpiRow');
  if (kpi) kpi.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(String(msg))}</div>`;
  ['instQuartChart', 'instYearlyContainer', 'instRegionTable', 'instBuyerMixContainer'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.innerHTML = '';
  });
}

// ---------------------------------------------------------------------------
// KPI row
// ---------------------------------------------------------------------------

function renderKpis(d: MarketDashboard): void {
  const el = document.getElementById('instKpiRow');
  if (!el) return;

  const modeLabel = INST_MODE === 'sold' ? t('inst_count_sold') : t('inst_count_unsold');
  const priceLabel = INST_MODE === 'sold' ? t('inst_price_sold') : t('inst_price_unsold');

  const heading = d.kpis.n_years > 0
    ? t('inst_kpi_avg_title', { y1: d.year_min, y2: d.year_max })
    : t('inst_kpi_no_full_years');

  el.innerHTML = `
    <div class="inst-kpi-heading">${escapeHtml(heading)}</div>
    <div class="inst-kpi-grid">
      <div class="inst-kpi featured">
        <div class="inst-kpi-label">${escapeHtml(INST_MODE === 'sold' ? t('inst_kpi_sold_count') : t('inst_kpi_unsold_count'))}</div>
        <div class="inst-kpi-value">${escapeHtml(fmt(Math.round(d.kpis.avg_count)))}</div>
        <div class="inst-kpi-unit">${escapeHtml(t('inst_kpi_unit_count', { mode: modeLabel }))}</div>
      </div>
      <div class="inst-kpi">
        <div class="inst-kpi-label">${escapeHtml(t('inst_kpi_total_ha'))}</div>
        <div class="inst-kpi-value">${escapeHtml(fmt(d.kpis.avg_ha, 0))}</div>
        <div class="inst-kpi-unit">${escapeHtml(t('inst_kpi_unit_ha'))}</div>
      </div>
      <div class="inst-kpi">
        <div class="inst-kpi-label">${escapeHtml(t('inst_kpi_total_value', { label: priceLabel.toLowerCase() }))}</div>
        <div class="inst-kpi-value">${escapeHtml(fmtMillions(d.kpis.avg_price))}</div>
        <div class="inst-kpi-unit">${escapeHtml(t('inst_kpi_unit_eur'))}</div>
      </div>
      <div class="inst-kpi">
        <div class="inst-kpi-label">${escapeHtml(t('inst_kpi_avg_eur_ha'))}</div>
        <div class="inst-kpi-value">${d.kpis.avg_eur_ha > 0 ? escapeHtml(fmt(d.kpis.avg_eur_ha, 0)) : '—'}</div>
        <div class="inst-kpi-unit">${escapeHtml(t('inst_kpi_unit_eur_ha'))}</div>
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Quarterly chart — twin bars with dual y-axes (mockup renderInstQuarterly).
// ---------------------------------------------------------------------------

function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(value)));
  const norm = value / exp;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 2.5) nice = 2.5;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * exp;
}

function renderQuarters(d: MarketDashboard): void {
  const sub = document.getElementById('instQuartSub');
  const chart = document.getElementById('instQuartChart');
  if (!sub || !chart) return;

  const qs = d.quarters || [];
  if (qs.length === 0) {
    sub.textContent = t('inst_quart_no_data');
    chart.innerHTML = `<div style="padding:30px;text-align:center;color:var(--muted);font-family:var(--mono);font-size:12px">${escapeHtml(t('inst_quart_no_data'))}</div>`;
    return;
  }

  sub.textContent = t('inst_quart_sub', { n: qs.length, first: qs[0].key, last: qs[qs.length - 1].key });

  const maxHa = Math.max(1, ...qs.map((b) => b.ha));
  const maxSumM = Math.max(0.1, ...qs.map((b) => b.sum / 1e6));
  const axisHa = niceCeil(maxHa);
  const axisEur = niceCeil(maxSumM);

  const countLabel = INST_MODE === 'sold' ? t('inst_count_sold') : t('inst_count_unsold');

  const cols = qs
    .map((b) => {
      const haH = Math.max(2, (b.ha / axisHa) * 100);
      const sumH = Math.max(2, (b.sum / 1e6 / axisEur) * 100);
      const sumDisplay = (b.sum / 1e6).toFixed(b.sum < 10e6 ? 2 : 1).replace('.', ',');
      return `
        <div class="inst-quart-col">
          <div class="inst-quart-tooltip">
            <strong>${escapeHtml(b.key)}</strong><br>
            ${escapeHtml(fmt(b.count))} ${escapeHtml(countLabel)}<br>
            ${escapeHtml(fmt(b.ha, 0))} ha<br>
            ${escapeHtml(sumDisplay)} M€
          </div>
          <div class="inst-quart-bars">
            <div class="inst-quart-bar ha-bar" style="height:${haH.toFixed(1)}%"></div>
            <div class="inst-quart-bar sum-bar" style="height:${sumH.toFixed(1)}%"></div>
          </div>
          <div class="inst-quart-label">${escapeHtml(b.key)}</div>
        </div>
      `;
    })
    .join('');

  const haTicks = buildTicks(axisHa, (v) => fmt(Math.round(v)));
  const eurTicks = buildTicks(axisEur, (v) => (v >= 10 ? Math.round(v) + ' M€' : v.toFixed(1).replace('.', ',') + ' M€'));

  chart.innerHTML = `
    <div class="inst-axis inst-axis-left">${haTicks}</div>
    <div class="inst-chart-cols">${cols}</div>
    <div class="inst-axis inst-axis-right">${eurTicks}</div>
  `;
}

function buildTicks(max: number, fmtFn: (v: number) => string): string {
  const ticks: string[] = [];
  for (let i = 4; i >= 0; i--) {
    const v = (max * i) / 4;
    ticks.push(`<div class="inst-tick">${escapeHtml(fmtFn(v))}</div>`);
  }
  return ticks.join('');
}

// ---------------------------------------------------------------------------
// Yearly panels
// ---------------------------------------------------------------------------

function renderYearly(d: MarketDashboard): void {
  const cnt = document.getElementById('instYearlyContainer');
  const sub = document.getElementById('instYearlySub');
  if (!cnt || !sub) return;
  const years = d.years || [];
  if (years.length === 0) {
    cnt.innerHTML = `<div style="padding:30px;text-align:center;color:var(--muted);font-family:var(--mono);font-size:12px">${escapeHtml(t('inst_no_data'))}</div>`;
    sub.textContent = '';
    return;
  }
  sub.textContent = t('inst_yearly_sub', { n: years.length });

  const countLabel = INST_MODE === 'sold' ? t('inst_count_sold') : t('inst_count_unsold');

  cnt.innerHTML = years
    .map((ye) => {
      const topRegions = (ye.top_regions || []).map((r) => `${r.region} (${r.count})`).join(', ');
      const totalBuyers = ye.inst_count + ye.priv_count;
      const buyerLine = INST_MODE === 'sold' && totalBuyers > 0
        ? `<div class="inst-year-extra">${t('inst_year_buyers', {
            inst: ye.inst_count,
            priv: ye.priv_count,
            pct: Math.round((ye.inst_count / totalBuyers) * 100),
          })}</div>`
        : '';
      const regionLine = topRegions
        ? `<div class="inst-year-extra">${t('inst_year_top_regions', { regions: escapeHtml(topRegions) })}</div>`
        : '';
      const sumLabel = INST_MODE === 'sold' ? t('inst_year_total_sum_sold') : t('inst_year_total_sum_unsold');
      return `
        <div class="inst-year-card">
          <div class="inst-year-header">
            <div class="inst-year-num">${ye.year}</div>
            <div class="inst-year-count">${escapeHtml(fmt(ye.count))} ${escapeHtml(countLabel)}</div>
          </div>
          <div class="inst-year-grid">
            <div class="inst-year-cell">
              <div class="inst-year-label">${escapeHtml(t('inst_year_total_ha'))}</div>
              <div class="inst-year-value">${escapeHtml(fmt(ye.total_ha, 0))} ha</div>
            </div>
            <div class="inst-year-cell">
              <div class="inst-year-label">${escapeHtml(sumLabel)}</div>
              <div class="inst-year-value">${escapeHtml(fmtMillions(ye.total_sum))} M€</div>
            </div>
            <div class="inst-year-cell">
              <div class="inst-year-label">${escapeHtml(t('inst_year_median_eurha'))}</div>
              <div class="inst-year-value">${ye.median_eur_ha ? escapeHtml(fmt(ye.median_eur_ha, 0)) + ' €' : '—'}</div>
            </div>
            <div class="inst-year-cell">
              <div class="inst-year-label">${escapeHtml(t('inst_year_median_m3'))}</div>
              <div class="inst-year-value">${ye.median_m3_per_ha ? escapeHtml(fmt(ye.median_m3_per_ha, 0)) : '—'}</div>
            </div>
            <div class="inst-year-cell">
              <div class="inst-year-label">${escapeHtml(t('inst_year_median_tukki'))}</div>
              <div class="inst-year-value">${ye.median_tukki_pct ? escapeHtml(fmt(ye.median_tukki_pct, 0)) + ' %' : '—'}</div>
            </div>
          </div>
          ${regionLine}
          ${buyerLine}
        </div>
      `;
    })
    .join('');
}

// ---------------------------------------------------------------------------
// Region table
// ---------------------------------------------------------------------------

function renderRegions(d: MarketDashboard): void {
  const el = document.getElementById('instRegionTable');
  if (!el) return;
  const rows = d.regions || [];
  if (rows.length === 0) {
    el.innerHTML = `<div style="padding:18px;color:var(--muted);font-family:var(--mono);font-size:12px">${escapeHtml(t('inst_no_data'))}</div>`;
    return;
  }
  const sumTh = INST_MODE === 'sold' ? t('inst_region_th_sum_sold') : t('inst_region_th_sum_unsold');
  el.innerHTML = `
    <table class="inst-region-table">
      <thead><tr>
        <th>${escapeHtml(t('inst_region_th_region'))}</th>
        <th class="num">${escapeHtml(t('inst_region_th_count'))}</th>
        <th class="num">${escapeHtml(t('inst_region_th_ha'))}</th>
        <th class="num">${escapeHtml(sumTh)}</th>
        <th class="num">${escapeHtml(t('inst_region_th_eurha'))}</th>
      </tr></thead>
      <tbody>
        ${rows
          .map(
            (r) => `<tr>
              <td>${escapeHtml(r.region)}</td>
              <td class="num">${escapeHtml(fmt(r.count))}</td>
              <td class="num">${escapeHtml(fmt(r.ha, 0))}</td>
              <td class="num">${escapeHtml(fmtMillions(r.sum))}</td>
              <td class="num">${r.eur_ha ? escapeHtml(fmt(r.eur_ha, 0)) : '—'}</td>
            </tr>`,
          )
          .join('')}
      </tbody>
    </table>
  `;
}

// ---------------------------------------------------------------------------
// Buyer mix (sold mode only)
// ---------------------------------------------------------------------------

function renderBuyerMix(d: MarketDashboard): void {
  const section = document.getElementById('instBuyerMixSection');
  const cnt = document.getElementById('instBuyerMixContainer');
  if (!section || !cnt) return;
  if (INST_MODE !== 'sold') {
    section.style.display = 'none';
    return;
  }
  section.style.display = '';
  const years = d.buyer_mix || [];
  if (years.length === 0) {
    cnt.innerHTML = `<div style="padding:18px;color:var(--muted);font-family:var(--mono);font-size:12px">${escapeHtml(t('inst_buyer_no_data'))}</div>`;
    return;
  }
  cnt.innerHTML = years
    .map((y) => {
      const total = y.inst_n + y.priv_n;
      const instPct = total > 0 ? (y.inst_n / total) * 100 : 0;
      return `
        <div class="inst-buyer-row">
          <div class="inst-buyer-year">${y.year}</div>
          <div class="inst-buyer-bar-wrap">
            <div class="inst-buyer-bar-inst" style="width:${instPct.toFixed(1)}%"></div>
          </div>
          <div class="inst-buyer-info">${escapeHtml(
            t('inst_buyer_info', { pct: instPct.toFixed(0), inst: y.inst_n, total }),
          )}</div>
        </div>
      `;
    })
    .join('');
}

// ---------------------------------------------------------------------------
// Number / string helpers
// ---------------------------------------------------------------------------

function fmt(n: number, decimals = 0): string {
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat(getLang() === 'en' ? 'en-FI' : 'fi-FI', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

// Mockup `fmtMillions`: prints € sums as M€ with locale comma.
function fmtMillions(n: number): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return (n / 1e9).toFixed(2).replace('.', ',') + ' Mrd';
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace('.', ',');
  if (n >= 1e3) return (n / 1e6).toFixed(2).replace('.', ',');
  return (n / 1e6).toFixed(3).replace('.', ',');
}

function escapeHtml(s: string | number): string {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]!));
}
